import React, { useMemo, useState } from 'react';
import { formatISODate, isoWeek, isoWeekKey, parseISODate } from '../utils/dates';

function saleOptionLabel(s) {
  return `#${s.id} · ${formatISODate(s.date)} · ${s.farm_name} · ${s.boxes.toLocaleString('es-EC')} cajas`;
}

// The "ID DE VENTA" row used by the forms built on top of a sale (Remisión,
// Detalles de Cosecha), plus its hint rows. With no search, the dropdown lists
// the sales from the same ISO week as the form's `date`; typing an ID searches
// all sales, and Enter selects the exact match. The selected sale always stays
// listed so an older one (e.g. when editing) isn't dropped. `exclude` hides
// sales already added elsewhere (Detalles de Cosecha holds several).
export default function SalePickerRows({ sales, value, onSelect, date, label = 'ID DE VENTA', exclude = [] }) {
  const [search, setSearch] = useState('');
  const query = search.trim();

  const weekKey = date ? isoWeekKey(parseISODate(date)) : null;
  const weekSales = useMemo(
    () => sales.filter((s) => weekKey && isoWeekKey(parseISODate(s.date)) === weekKey
      && !exclude.includes(String(s.id))),
    [sales, weekKey, exclude],
  );
  const visibleSales = useMemo(() => {
    const matches = query
      ? sales.filter((s) => String(s.id).startsWith(query) && !exclude.includes(String(s.id)))
      : weekSales;
    const selected = sales.find((s) => String(s.id) === value);
    return selected && !matches.includes(selected) ? [selected, ...matches] : matches;
  }, [sales, weekSales, query, value, exclude]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const exact = sales.find((s) => String(s.id) === query && !exclude.includes(query));
    if (exact) {
      onSelect(String(exact.id));
      setSearch('');
    }
  };

  const hint = (text) => (
    <tr>
      <td />
      <td className="sales-value remision-sale-empty">{text}</td>
    </tr>
  );

  return (
    <>
      <tr>
        <td className="sales-label">{label}</td>
        <td className="sales-value">
          <div className="remision-sale-picker">
            <input
              className="remision-sale-search"
              inputMode="numeric"
              placeholder="🔍 ID"
              value={search}
              onChange={(e) => setSearch(e.target.value.replace(/\D/g, ''))}
              onKeyDown={handleKeyDown}
              aria-label="Buscar venta por ID"
              title="Escriba el ID de la venta y presione Enter"
            />
            <select className="sales-cell-input" value={value} onChange={(e) => onSelect(e.target.value)}>
              <option value="">— seleccionar —</option>
              {visibleSales.map((s) => <option key={s.id} value={s.id}>{saleOptionLabel(s)}</option>)}
            </select>
          </div>
        </td>
      </tr>
      {query && visibleSales.every((s) => !String(s.id).startsWith(query)) && hint('No hay ventas con ese ID')}
      {!query && weekSales.length === 0 && date
        && hint(`No hay ${exclude.length ? 'más ' : ''}ventas en la semana ${isoWeek(parseISODate(date))}. `
          + 'Busque la venta por su ID.')}
    </>
  );
}
